import React, { useState } from 'react';
import './RecoveryCard.css';
import InfoModal, { InfoButton } from './InfoModal';

// Anel circular preenchendo o contorno conforme o score, de 0 a 100.
function RecoveryRing({ score, color }) {
  const size = 128, stroke = 11, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, score)) / 100) * c;
  return (
    <svg className="rc-ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle" className="rc-ring-score" fill={color}>
        {score}%
      </text>
      <text x="50%" y="66%" textAnchor="middle" dominantBaseline="middle" className="rc-ring-label">
        recuperação
      </text>
    </svg>
  );
}

const fmtGeradoEm = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

function RecoveryCard({ data }) {
  const [showInfo, setShowInfo] = useState(false);

  if (!data) return null;

  if (!data.disponivel) {
    return (
      <section className="card rc-card">
        <h3>🔋 Recuperação</h3>
        <p className="rc-empty">{data.motivo || 'Sem dados suficientes ainda.'}</p>
        {data.gerado_em && <p className="rc-updated">Última análise: {fmtGeradoEm(data.gerado_em)}</p>}
      </section>
    );
  }

  const { score, nivel, cor, emoji, estresse_estimado, horas_descanso_sugeridas_display, recomendacao, componentes } = data;
  const c = componentes || {};

  return (
    <section className="card rc-card">
      <div className="rc-title-row">
        <h3>🔋 Recuperação</h3>
        <InfoButton onClick={() => setShowInfo(true)} />
      </div>
      {data.gerado_em && <p className="rc-updated">Última análise: {fmtGeradoEm(data.gerado_em)}</p>}
      {showInfo && (
        <InfoModal title="Como a Recuperação é calculada" onClose={() => setShowInfo(false)}>
          <p>
            Estima o quão recuperado seu corpo está hoje, combinando 4 componentes do
            seu próprio histórico.
          </p>
          <p><b>HRV (40%)</b>: variabilidade da frequência cardíaca de hoje comparada com
            sua média/desvio padrão dos últimos 30 dias. HRV mais alto que o normal é melhor.</p>
          <p><b>FC de repouso (30%)</b>: mesma lógica, só que invertida: frequência cardíaca
            mais alta que o seu normal é pior recuperação.</p>
          <p><b>Sono (20%)</b>: horas da última noite comparadas com uma meta fixa de 7h30,
            não com sua média pessoal (pra não "premiar" quem dorme mal sempre).</p>
          <p><b>Carga de treino (10%)</b>: minutos de atividade de ontem e hoje comparados com
            sua média diária dos últimos 14 dias. Treino recente acima do normal reduz o
            score, já que o corpo ainda está absorvendo o esforço.</p>
          <p>Cada componente vira um z score (posição em relação à sua própria média/desvio
            padrão) e a combinação pesada dá o score de 0 a 100. Faixas: 67% ou mais é Alta,
            entre 34% e 66% é Moderada, abaixo de 34% é Baixa. Precisa de pelo menos 5 dias de
            histórico por componente pra calcular. Sem isso, esse componente não entra
            na conta.</p>
          <p>Não é diagnóstico médico, é só uma estimativa de bem estar a partir dos seus
            próprios dados.</p>
        </InfoModal>
      )}
      <div className="rc-main">
        <RecoveryRing score={score} color={cor} />
        <div className="rc-summary">
          <span className="rc-nivel" style={{ color: cor }}>{emoji} {nivel}</span>
          <p className="rc-reco">{recomendacao}</p>
          <div className="rc-mini-stats">
            <span>Estresse estimado: <b>{estresse_estimado}%</b></span>
            {horas_descanso_sugeridas_display && (
              <span>Descanso sugerido: <b>{horas_descanso_sugeridas_display}</b></span>
            )}
          </div>
        </div>
      </div>

      <div className="rc-breakdown">
        {c.hrv && (
          <div className="rc-row">
            <span className="rc-row-label">Variabilidade FC (HRV)</span>
            <span className="rc-row-value">{c.hrv.hoje} ms <small>(méd. {c.hrv.media} ms)</small></span>
          </div>
        )}
        {c.fc_repouso && (
          <div className="rc-row">
            <span className="rc-row-label">FC de Repouso</span>
            <span className="rc-row-value">{c.fc_repouso.hoje} bpm <small>(méd. {c.fc_repouso.media} bpm)</small></span>
          </div>
        )}
        {c.sono && (
          <div className="rc-row">
            <span className="rc-row-label">Sono (última noite)</span>
            <span className="rc-row-value">{c.sono.horas_display} <small>(meta {c.sono.meta_horas_display})</small></span>
          </div>
        )}
        {c.carga_treino && (
          <div className="rc-row">
            <span className="rc-row-label">Carga de treino recente</span>
            <span className="rc-row-value">{c.carga_treino.minutos_recentes} min <small>(méd. {c.carga_treino.media_diaria_min}/dia)</small></span>
          </div>
        )}
      </div>

      <p className="rc-disclaimer">
        Estimativa de bem estar (tipo wearable) a partir dos seus próprios dados. Não é diagnóstico médico.
      </p>
    </section>
  );
}

export default RecoveryCard;
